
import { GoogleGenAI, LiveServerMessage, Modality, Blob } from '@google/genai';
import { Product } from '../types';

export interface LiveChatHandlers {
  onAudioData: (data: Float32Array) => void;
  onTranscription: (text: string, isUser: boolean) => void;
  onInterrupted: () => void;
  onError: (msg: string) => void;
}

export class LiveChatService {
  private sessionPromise: Promise<any> | null = null;
  private audioContext: AudioContext | null = null;
  private nextStartTime: number = 0;
  private activeSources: Set<AudioBufferSourceNode> = new Set();

  constructor() {}

  async start(storeName: string, products: Product[], handlers: LiveChatHandlers) {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
    const inputContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
    
    const inventoryText = products.map(p => `- ${p.name}: $${p.price}`).join('\n');
    const systemInstruction = `You are Lynq AI v2, the ultimate premium tech guru for "${storeName}". 
    You can SEE products via the camera and HEAR the customer.
    Current Inventory:
    ${inventoryText}
    
    Rules:
    1. Be brief, tech-savvy, and high-energy.
    2. If a customer shows a product, identify it and talk about its value.
    3. Use a friendly but professional "Apple Genius" style tone.`;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    this.sessionPromise = (ai as any).live.connect({
      model: 'gemini-2.5-flash-native-audio-preview-12-2025',
      callbacks: {
        onopen: () => {
          const source = inputContext.createMediaStreamSource(stream);
          const processor = inputContext.createScriptProcessor(4096, 1, 1);
          processor.onaudioprocess = (e) => {
            const inputData = e.inputBuffer.getChannelData(0);
            this.sessionPromise?.then(session => {
              session.sendRealtimeInput({ media: this.createPcmBlob(inputData) });
            });
          };
          source.connect(processor);
          processor.connect(inputContext.destination);
        },
        onmessage: async (message: LiveServerMessage) => {
          if (message.serverContent?.modelTurn?.parts[0]?.inlineData) {
            const base64Audio = message.serverContent.modelTurn.parts[0].inlineData.data;
            this.playAudio(base64Audio);
          }
          
          if (message.serverContent?.outputTranscription) {
            handlers.onTranscription(message.serverContent.outputTranscription.text, false);
          }
          if (message.serverContent?.inputTranscription) {
            handlers.onTranscription(message.serverContent.inputTranscription.text, true);
          }
          
          if (message.serverContent?.interrupted) {
            this.stopAllAudio();
            handlers.onInterrupted();
          }
        },
        onerror: (e: any) => handlers.onError("Live connection failed."),
        onclose: () => console.log("Live session closed")
      },
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } }
        },
        systemInstruction,
        outputAudioTranscription: {},
        inputAudioTranscription: {}
      }
    });
  }

  sendFrame(base64Data: string) {
    this.sessionPromise?.then(session => {
      session.sendRealtimeInput({
        media: { data: base64Data, mimeType: 'image/jpeg' }
      });
    });
  }

  private createPcmBlob(data: Float32Array): Blob {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) {
      int16[i] = data[i] * 32768;
    }
    const binary = String.fromCharCode(...new Uint8Array(int16.buffer));
    return {
      data: btoa(binary),
      mimeType: 'audio/pcm;rate=16000'
    };
  }

  private async playAudio(base64: string) {
    if (!this.audioContext) return;
    
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    const dataInt16 = new Int16Array(bytes.buffer);
    const buffer = this.audioContext.createBuffer(1, dataInt16.length, 24000);
    const channelData = buffer.getChannelData(0);
    for (let i = 0; i < dataInt16.length; i++) {
      channelData[i] = dataInt16[i] / 32768.0;
    }

    this.nextStartTime = Math.max(this.nextStartTime, this.audioContext.currentTime);
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);
    source.start(this.nextStartTime);
    this.nextStartTime += buffer.duration;
    this.activeSources.add(source);
    source.onended = () => this.activeSources.delete(source);
  }

  private stopAllAudio() {
    this.activeSources.forEach(s => s.stop());
    this.activeSources.clear();
    this.nextStartTime = 0;
  }

  stop() {
    this.sessionPromise?.then(s => s.close());
    this.stopAllAudio();
  }
}
